import Button from '../../button/Button';
import TextInput from '../../input/text-input/TextInput';
import React, { useEffect, useMemo, useState } from 'react';

import { TrackingDeviceAssignment } from '../../../../types/entity.types';

import useToaster from '../../../../hooks/use-toaster/useToaster';

import Modal from '../Modal';

import './device-assignment-modal.scss';

type Props = {
  systemUserExternalReference: string;
  show: boolean;
  close: () => void;
  createTrackingDeviceAssignment: (
    systemUserExternalReference: string,
    deviceId: string,
    onSuccess?: (data: TrackingDeviceAssignment) => void
  ) => void;
  retrieveTrackingDeviceAssignments: (
    systemUserExternalReference: string,
    onSuccess?: (data: TrackingDeviceAssignment[]) => void
  ) => void;
};

const DeviceAssignmentModal = ({
  systemUserExternalReference,
  show,
  close,
  createTrackingDeviceAssignment,
  retrieveTrackingDeviceAssignments
}: Props) => {
  const [devices, setDevices] = useState<TrackingDeviceAssignment[]>([]);
  const [showNewDeviceModal, setShowNewDeviceModal] = useState(false);
  const [deviceId, setDeviceId] = useState('');

  const { showToaster } = useToaster();

  useEffect(() => {
    if (!show) return;

    retrieveTrackingDeviceAssignments(systemUserExternalReference, (data) => {
      setDevices(data);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, systemUserExternalReference]);

  const [currentDevice, previousDevices] = useMemo(() => {
    if (devices.length === 0) return [null, []];

    const currentDevice = devices.find((device) => !device.endDate);
    const previousDevices = devices.filter((device) => device.endDate);

    return [currentDevice, previousDevices];
  }, [devices]);

  const handleSaveClick = () => {
    if (!deviceId) {
      showToaster('error', 'Please enter a device ID');
      return;
    }

    createTrackingDeviceAssignment(systemUserExternalReference, deviceId, () => {
      setShowNewDeviceModal(false);
      setDeviceId('');

      retrieveTrackingDeviceAssignments(systemUserExternalReference, (data) => {
        setDevices(data);
      });
    });
  };

  const handleCloseNewDeviceModalClick = () => {
    setShowNewDeviceModal(false);
    setDeviceId('');
  };

  return (
    <>
      <Modal show={show && !showNewDeviceModal}>
        <section className="device-assignment-modal">
          <div className="device-assignment-modal__header">Device ID</div>
          <div className="device-assignment-modal__content">
            {devices.length > 0 ? (
              <>
                <div className="device-assignment-modal__content__current-device">
                  <p>Current Device ID:</p>
                  <p>{currentDevice?.deviceId ?? '-'}</p>
                  <p>Activation Date:</p>
                  <p>{formatDate(currentDevice?.startDate)}</p>
                </div>
                <div className="device-assignment-modal__content__previous-devices">
                  <p>Previous Devices</p>
                  {previousDevices.length > 0 ? (
                    <>
                      <div className="device-assignment-modal__content__previous-devices__header">
                        <p>ID</p>
                        <p>Activation Date</p>
                        <p>Deactivation Date</p>
                      </div>
                      <div className="device-assignment-modal__content__previous-devices__scroll-container">
                        {previousDevices.map((device) => (
                          <div
                            key={device.externalReference}
                            className="device-assignment-modal__content__previous-devices__row"
                          >
                            <p>{device.deviceId}</p>
                            <p>{formatDate(device.startDate)}</p>
                            <p>{formatDate(device.endDate)}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className="device-assignment-modal__content__no-devices">No previously assigned devices</p>
                  )}
                </div>
              </>
            ) : (
              <div className="device-assignment-modal__content__no-devices">No assigned devices</div>
            )}
          </div>
          <div className="device-assignment-modal__button-container">
            <Button text="Cancel" onClick={close} />
            <Button text="Activate New Device" onClick={() => setShowNewDeviceModal(true)} />
          </div>
        </section>
      </Modal>

      <Modal show={showNewDeviceModal}>
        <section className="device-assignment-modal">
          <div className="device-assignment-modal__header">New Device</div>
          <div className="device-assignment-modal__content">
            <div className="device-assignment-modal__content__new-device">
              <TextInput
                label="Enter new Device ID:"
                name="device-id"
                value={deviceId}
                onChange={({ target }) => setDeviceId(target.value)}
              />
            </div>
          </div>
          <div className="device-assignment-modal__button-container">
            <Button text="Cancel" onClick={handleCloseNewDeviceModalClick} />
            <Button text="Save" onClick={handleSaveClick} />
          </div>
        </section>
      </Modal>
    </>
  );
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';

  const date = new Date(dateString);
  return date.toLocaleDateString();
};

export default DeviceAssignmentModal;
